import { ArrowLeft, Loader2, Trash2 } from "lucide-react"
import * as React from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { ApiError, api, toastApiError } from "@/lib/api"
import type { Connection, McpTool, Provider, ResponseStyle } from "@/lib/types"

interface Selection {
  connectionId: string
  /** "*" means every tool this server exposes, including ones added later. */
  toolNames: Set<string> | "all"
}

/**
 * Admin-only agent editor that configures an agent on behalf of another user.
 *
 * A trimmed sibling of the self-service `agent-editor`: it drives the admin CRUD
 * endpoints (so the agent is owned by the target user), always shows the model
 * picker (an admin is always the acting party), and omits the plugin / Telegram /
 * memory tabs, which remain the user's own to manage.
 */
export function AdminAgentEditorPage() {
  const { userId, agentId } = useParams()
  const navigate = useNavigate()
  const isNew = !agentId
  const backTo = `/admin/users/${userId}`

  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [providers, setProviders] = React.useState<Provider[]>([])
  const [connections, setConnections] = React.useState<Connection[]>([])
  const [toolsByConnection, setToolsByConnection] = React.useState<Record<string, McpTool[]>>({})

  const [name, setName] = React.useState("")
  const [description, setDescription] = React.useState("")
  const [providerKey, setProviderKey] = React.useState("")
  const [model, setModel] = React.useState("")
  const [systemPrompt, setSystemPrompt] = React.useState("")
  const [temperature, setTemperature] = React.useState("")
  const [maxIterations, setMaxIterations] = React.useState("8")
  const [isActive, setIsActive] = React.useState(true)
  const [webToolsEnabled, setWebToolsEnabled] = React.useState(true)
  const [responseStyle, setResponseStyle] = React.useState<ResponseStyle>("concise")
  const [selections, setSelections] = React.useState<Record<string, Selection>>({})

  React.useEffect(() => {
    if (!userId) return
    async function load() {
      try {
        const [providerList, connectionList] = await Promise.all([
          api.providers(),
          api.adminUserConnections(userId!),
        ])
        setProviders(providerList)
        setConnections(connectionList)

        const tools: Record<string, McpTool[]> = {}
        await Promise.all(
          connectionList.map(async (connection) => {
            tools[connection.id] = await api.adminUserConnectionTools(userId!, connection.id)
          }),
        )
        setToolsByConnection(tools)

        if (agentId) {
          const agent = (await api.adminUserAgents(userId!)).find((a) => a.id === agentId)
          if (!agent) {
            toast.error("Agent not found for this user.")
            navigate(backTo)
            return
          }
          setName(agent.name)
          setDescription(agent.description ?? "")
          setProviderKey(agent.provider_key)
          setModel(agent.model)
          setSystemPrompt(agent.system_prompt ?? "")
          setTemperature(agent.temperature?.toString() ?? "")
          setMaxIterations(agent.max_iterations.toString())
          setIsActive(agent.is_active)
          setWebToolsEnabled(agent.web_tools_enabled)
          setResponseStyle(agent.response_style)

          const next: Record<string, Selection> = {}
          for (const binding of agent.tools) {
            const existing = next[binding.connection_id]
            if (binding.tool_name === "*") {
              next[binding.connection_id] = { connectionId: binding.connection_id, toolNames: "all" }
            } else if (!existing || existing.toolNames !== "all") {
              const set =
                existing && existing.toolNames !== "all" ? existing.toolNames : new Set<string>()
              set.add(binding.tool_name)
              next[binding.connection_id] = { connectionId: binding.connection_id, toolNames: set }
            }
          }
          setSelections(next)
        } else {
          const ready = providerList.find((provider) => provider.ready) ?? providerList[0]
          if (ready) {
            setProviderKey(ready.provider_key)
            setModel(ready.default_model ?? "")
          }
        }
      } catch (error) {
        toast.error(error instanceof ApiError ? error.message : "Could not load the editor.")
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [userId, agentId, navigate, backTo])

  const provider = providers.find((entry) => entry.provider_key === providerKey)

  function toggleConnection(connection: Connection, enabled: boolean) {
    setSelections((current) => {
      const next = { ...current }
      if (enabled) next[connection.id] = { connectionId: connection.id, toolNames: "all" }
      else delete next[connection.id]
      return next
    })
  }

  function toggleTool(connection: Connection, toolName: string) {
    setSelections((current) => {
      const next = { ...current }
      const entry = next[connection.id]
      const all = (toolsByConnection[connection.id] ?? []).map((tool) => tool.name)
      const set =
        !entry || entry.toolNames === "all"
          ? new Set<string>(entry?.toolNames === "all" ? all : [])
          : new Set(entry.toolNames)

      if (set.has(toolName)) set.delete(toolName)
      else set.add(toolName)

      if (set.size === 0) delete next[connection.id]
      else next[connection.id] = { connectionId: connection.id, toolNames: set }
      return next
    })
  }

  function buildToolPayload() {
    const payload: { connection_id: string; tool_name: string }[] = []
    for (const selection of Object.values(selections)) {
      if (selection.toolNames === "all") {
        payload.push({ connection_id: selection.connectionId, tool_name: "*" })
      } else {
        for (const toolName of selection.toolNames) {
          payload.push({ connection_id: selection.connectionId, tool_name: toolName })
        }
      }
    }
    return payload
  }

  async function save(event: React.FormEvent) {
    event.preventDefault()
    if (!userId) return
    setSaving(true)
    const payload = {
      name,
      description: description || null,
      provider_key: providerKey,
      model: model || null,
      system_prompt: systemPrompt || null,
      temperature: temperature === "" ? null : Number(temperature),
      max_iterations: Number(maxIterations),
      is_active: isActive,
      web_tools_enabled: webToolsEnabled,
      response_style: responseStyle,
      tools: buildToolPayload(),
    }
    try {
      const agent = agentId
        ? await api.adminUpdateUserAgent(userId, agentId, payload)
        : await api.adminCreateUserAgent(userId, payload)
      toast.success(`Saved "${agent.name}".`)
      navigate(backTo)
    } catch (error) {
      toastApiError(error, "Could not save the agent.")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="text-muted-foreground p-6 text-sm">Loading…</div>
  }

  return (
    <>
      <PageHeader
        title={isNew ? "New agent" : name || "Edit agent"}
        description="Configure this agent on behalf of the user. The agent is owned by them."
        action={
          <Button variant="outline" onClick={() => navigate(backTo)}>
            <ArrowLeft className="size-4" />
            Back to user
          </Button>
        }
      />

      <div className="p-6 pb-24">
        <form onSubmit={save} className="space-y-6">
          {providers.length === 0 && (
            <Alert variant="destructive">
              <AlertTitle>No model provider configured</AlertTitle>
              <AlertDescription>
                This server has no model provider configured, so agents cannot run yet.
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Basics</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  required
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Support Assistant"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="What this agent is for"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="prompt">System prompt</Label>
                <Textarea
                  id="prompt"
                  rows={6}
                  value={systemPrompt}
                  onChange={(event) => setSystemPrompt(event.target.value)}
                  placeholder="You are a helpful assistant."
                />
              </div>
              <div className="flex items-center gap-3 md:col-span-2">
                <Switch id="active" checked={isActive} onCheckedChange={setIsActive} />
                <Label htmlFor="active">Active</Label>
              </div>
              <div className="flex items-center gap-3 md:col-span-2">
                <Switch
                  id="web-tools"
                  checked={webToolsEnabled}
                  onCheckedChange={setWebToolsEnabled}
                />
                <Label htmlFor="web-tools">Web tools enabled</Label>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="response-style">Response style</Label>
                <Select
                  value={responseStyle}
                  onValueChange={(value) => setResponseStyle(value as ResponseStyle)}
                >
                  <SelectTrigger id="response-style" className="md:w-72">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="concise">Concise</SelectItem>
                    <SelectItem value="extended">Extended</SelectItem>
                    <SelectItem value="detailed">Detailed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Model</CardTitle>
              <CardDescription>Provider, model, and sampling for this agent.</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="provider">Provider</Label>
                <Select
                  value={providerKey}
                  onValueChange={(value) => {
                    setProviderKey(value)
                    const next = providers.find((entry) => entry.provider_key === value)
                    setModel(next?.default_model ?? "")
                  }}
                >
                  <SelectTrigger id="provider">
                    <SelectValue placeholder="Pick a provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {providers.map((entry) => (
                      <SelectItem key={entry.provider_key} value={entry.provider_key}>
                        {entry.label}
                        {entry.ready ? "" : " (no API key)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                {provider && provider.models.length > 0 ? (
                  <Select value={model} onValueChange={setModel}>
                    <SelectTrigger id="model">
                      <SelectValue placeholder="Pick a model" />
                    </SelectTrigger>
                    <SelectContent>
                      {provider.models.map((entry) => (
                        <SelectItem key={entry} value={entry}>
                          {entry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="model"
                    value={model}
                    onChange={(event) => setModel(event.target.value)}
                    placeholder="deepseek-flash"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="temperature">Temperature</Label>
                <Input
                  id="temperature"
                  type="number"
                  min={0}
                  max={2}
                  step={0.1}
                  value={temperature}
                  onChange={(event) => setTemperature(event.target.value)}
                  placeholder="default"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="iterations">Step limit</Label>
                <Input
                  id="iterations"
                  type="number"
                  min={1}
                  max={100}
                  value={maxIterations}
                  onChange={(event) => setMaxIterations(event.target.value)}
                />
                <p className="text-muted-foreground text-xs">
                  Max tool-use iterations per run.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Tools</CardTitle>
              <CardDescription>
                Bind the user's MCP servers to this agent. Manage the servers themselves from
                the user's connections page.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {connections.length === 0 ? (
                <Alert>
                  <AlertTitle>No tool servers</AlertTitle>
                  <AlertDescription>
                    This user has no MCP servers yet.
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0"
                      onClick={() => navigate(`/admin/users/${userId}/connections`)}
                    >
                      Manage their connections
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                connections.map((connection) => {
                  const selection = selections[connection.id]
                  const tools = (toolsByConnection[connection.id] ?? []).filter(
                    (tool) => !tool.unsupported_reason,
                  )
                  const allSelected = selection?.toolNames === "all"
                  return (
                    <div key={connection.id} className="rounded-lg border p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="font-medium">{connection.name}</div>
                          <div className="text-muted-foreground text-xs">
                            {tools.length} available
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`all-${connection.id}`} className="text-xs font-normal">
                            Use all
                          </Label>
                          <Switch
                            id={`all-${connection.id}`}
                            checked={allSelected}
                            onCheckedChange={(checked) => toggleConnection(connection, checked)}
                          />
                        </div>
                      </div>

                      {tools.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {tools.map((tool) => {
                            const picked =
                              allSelected ||
                              (selection?.toolNames !== "all" && selection?.toolNames.has(tool.name))
                            return (
                              <button
                                key={tool.id}
                                type="button"
                                onClick={() => toggleTool(connection, tool.name)}
                                title={tool.description ?? tool.name}
                              >
                                <Badge variant={picked ? "default" : "outline"}>{tool.name}</Badge>
                              </button>
                            )
                          })}
                        </div>
                      )}
                      {allSelected && (
                        <p className="text-muted-foreground mt-2 text-xs">
                          New tools from this server are included automatically.
                        </p>
                      )}
                    </div>
                  )
                })
              )}
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="size-4 animate-spin" /> : null}
              {saving ? "Saving…" : "Save agent"}
            </Button>
            {agentId && (
              <Button
                type="button"
                variant="ghost"
                className="text-destructive"
                onClick={async () => {
                  if (!confirm(`Delete "${name}"? This cannot be undone.`)) return
                  try {
                    await api.adminDeleteUserAgent(userId!, agentId)
                    toast.success("Agent deleted.")
                    navigate(backTo)
                  } catch (error) {
                    toast.error(error instanceof ApiError ? error.message : "Could not delete.")
                  }
                }}
              >
                <Trash2 className="size-4" />
                Delete
              </Button>
            )}
          </div>
        </form>
      </div>
    </>
  )
}
